var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { styled } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../config';
import { TABLE_HIERARCHY_COLORS } from '../../../enum';
import { RumEntityLabel } from '../../../observability/rumEntity';
import { MULTISKILL_COLOR } from '../../annual/constants';
import { TableRow } from '../../common';
import { ExpandableRowLabel, nextTableHierarchyDepth } from '../../common/tableHierarchy';
import { QueueType } from '../../multiskill/types';
import { FLAG } from '../constants';
import { DAILY_METRIC_TARGET_MAP, EXPANDABLE_METRIC_KEYS, NO_MULTISKILL_SECTION_METRIC_KEYS, QUEUE_ONLY_METRIC_KEYS, } from './constants';
import { DAILY_NEST_LEVELS, LEVEL_ROW_INDENTS, LEVEL_ROW_STICKIES, StyledLevelFirstCell } from './styled';
var isQueueMetricZero = function (queue, metricIndex) {
    var detail = queue.detail;
    if (!detail)
        return false;
    var hasData = false;
    for (var _i = 0, _a = Object.values(detail); _i < _a.length; _i++) {
        var dayData = _a[_i];
        if (!dayData)
            continue;
        for (var _b = 0, _c = Object.values(dayData); _b < _c.length; _b++) {
            var timeData = _c[_b];
            if (Array.isArray(timeData) && timeData.length > metricIndex) {
                hasData = true;
                var value = Number(timeData[metricIndex]);
                if (!Number.isNaN(value) && value > 0)
                    return false;
            }
        }
    }
    return hasData;
};
var isQueueProductiveHoursZero = function (queue) { return isQueueMetricZero(queue, 0); };
var getQueueName = function (queue) { return queue.queueName || queue.queue_name || ''; };
var getCountryName = function (country) {
    return country.countryName || country.country_name || '';
};
var getLocationName = function (location) {
    return location.locationName || location.location_name || '';
};
var getLobName = function (lob) { return lob.lobName || lob.lob_name || ''; };
var hasGroupedHierarchyRows = function (queuesByLob) { return !!(queuesByLob === null || queuesByLob === void 0 ? void 0 : queuesByLob.length); };
var getQueueTargetFlag = function (queue, metric) {
    return queue.metricTarget != null && queue.metricTarget === DAILY_METRIC_TARGET_MAP[metric.key] ? FLAG : '';
};
var isQueueOnlyMetric = function (metric) { return QUEUE_ONLY_METRIC_KEYS.includes(metric.key); };
var hidesMultiskillSection = function (metric) {
    return NO_MULTISKILL_SECTION_METRIC_KEYS.includes(metric.key);
};
var NormalWeightLabel = styled('span')({ fontWeight: 400 });
var getLocationKey = function (location, index) { var _a, _b, _c; return "".concat((_c = (_b = (_a = location.queueLocationId) !== null && _a !== void 0 ? _a : location.planLocationId) !== null && _b !== void 0 ? _b : getLocationName(location)) !== null && _c !== void 0 ? _c : 'location', "-").concat(index); };
var hasProductiveHoursData = function (queuesByLob) {
    if (!(queuesByLob === null || queuesByLob === void 0 ? void 0 : queuesByLob.length))
        return true;
    return queuesByLob.some(function (lob) { return lob.queues.some(function (queue) { return !isQueueProductiveHoursZero(queue); }); });
};
export var DailyMetricHierarchyRows = function (_a) {
    var visibleMetrics = _a.visibleMetrics, detail = _a.detail, countries = _a.countries, queuesByLob = _a.queuesByLob, bodyColor = _a.bodyColor, renderValueCells = _a.renderValueCells, renderBlankCells = _a.renderBlankCells;
    var t = useTranslation(['report', 'translation']).t;
    var _b = useState({}), expandedRows = _b[0], setExpandedRows = _b[1];
    var hasGroupedHierarchy = hasGroupedHierarchyRows(queuesByLob);
    var hasMultiskillQueues = (queuesByLob !== null && queuesByLob !== void 0 ? queuesByLob : []).some(function (lob) {
        return lob.queues.some(function (queue) { return queue.queueType === QueueType.Multiskill || queue.isMultiskillMember; });
    });
    var hasMultiskillGroups = (queuesByLob !== null && queuesByLob !== void 0 ? queuesByLob : []).some(function (lob) {
        return lob.queues.some(function (queue) { return queue.queueType === QueueType.Multiskill; });
    });
    var usesSeatsCountryHierarchy = function (metric) {
        return metric.key === 'seatsRequired' && hasMultiskillQueues && !hasMultiskillGroups && countries.length > 0;
    };
    var metricsToRender = visibleMetrics.filter(function (metric) { return metric.key !== 'productiveHours' || hasProductiveHoursData(queuesByLob); });
    var canExpandMetric = function (metric) {
        return isQueueOnlyMetric(metric) ? hasGroupedHierarchy : hasGroupedHierarchy || countries.length > 0;
    };
    var hasAnyExpansion = metricsToRender.some(function (m) { return EXPANDABLE_METRIC_KEYS.includes(m.key) && canExpandMetric(m); });
    var getCountryKey = function (parentKey, country, countryIndex) {
        return "".concat(parentKey, "|country:").concat(getCountryName(country) || countryIndex);
    };
    var getLobKey = function (metricKey, lob, lobIndex) { var _a; return "".concat(metricKey, "|lob:").concat((_a = lob.lobId) !== null && _a !== void 0 ? _a : 'unassigned', "|name:").concat(getLobName(lob) || lobIndex); };
    var getQueueKey = function (lobKey, queue, queueIndex) {
        var _a, _b;
        var queueId = (_a = queue.queueId) !== null && _a !== void 0 ? _a : getQueueName(queue);
        var subLobId = (_b = queue.subLobId) !== null && _b !== void 0 ? _b : queueIndex;
        return "".concat(lobKey, "|queue:").concat(queueId, "|sub:").concat(subLobId);
    };
    var getCountryExpansionKeys = function (parentKey, countryRows) {
        return countryRows.map(function (country, countryIndex) { return getCountryKey(parentKey, country, countryIndex); });
    };
    var getQueueExpansionKeys = function (lobKey, queues, metric) {
        return queues.flatMap(function (queue, queueIndex) {
            var queueKey = getQueueKey(lobKey, queue, queueIndex);
            if (isQueueOnlyMetric(metric))
                return [queueKey];
            return __spreadArray([queueKey], getCountryExpansionKeys(queueKey, queue.countries || []), true);
        });
    };
    var getMultiskillSectionKey = function (metricKey) { return "".concat(metricKey, "|multiSkill"); };
    var getMultiskillGroupQueues = function (metric) {
        return metric && hidesMultiskillSection(metric)
            ? []
            : (queuesByLob !== null && queuesByLob !== void 0 ? queuesByLob : []).flatMap(function (lob) { return lob.queues.filter(function (queue) { return queue.queueType === QueueType.Multiskill; }); });
    };
    var getVisibleQueues = function (metric, queueRows) {
        var abandonableOnly = metric.key === 'abandonment' ? queueRows.filter(function (queue) { return queue.isAbandonable; }) : queueRows;
        var allVisibleQueues = metric.key === 'productiveHours'
            ? abandonableOnly.filter(function (queue) { return !isQueueProductiveHoursZero(queue); })
            : abandonableOnly;
        return allVisibleQueues.filter(function (queue) { return queue.queueType !== QueueType.Multiskill; });
    };
    var getMultiskillGroupKey = function (metricKey, queue, groupIndex) {
        return "".concat(getMultiskillSectionKey(metricKey), "|group:").concat(getQueueName(queue), "|").concat(groupIndex);
    };
    var getMultiskillGroupExpansionKeys = function (metricKey, metric) {
        return getMultiskillGroupQueues(metric).flatMap(function (queue, groupIndex) {
            var queueKey = getMultiskillGroupKey(metricKey, queue, groupIndex);
            if (isQueueOnlyMetric(metric))
                return [queueKey];
            return __spreadArray([queueKey], getCountryExpansionKeys(queueKey, queue.countries || []), true);
        });
    };
    var getMultiskillExpansionKeys = function (metricKey, metric) { return __spreadArray([
        getMultiskillSectionKey(metricKey)
    ], getMultiskillGroupExpansionKeys(metricKey, metric), true); };
    var getMetricExpansionKeys = function (metric) {
        var _a;
        var metricKey = "metric:".concat(metric.key);
        if (usesSeatsCountryHierarchy(metric)) {
            return __spreadArray(__spreadArray([], getCountryExpansionKeys(metricKey, countries), true), getMultiskillExpansionKeys(metricKey, metric), true);
        }
        if (!hasGroupedHierarchy)
            return getCountryExpansionKeys(metricKey, countries);
        return ((_a = queuesByLob === null || queuesByLob === void 0 ? void 0 : queuesByLob.flatMap(function (lob, lobIndex) {
            var lobKey = getLobKey(metricKey, lob, lobIndex);
            return __spreadArray([lobKey], getQueueExpansionKeys(lobKey, getVisibleQueues(metric, lob.queues), metric), true);
        })) !== null && _a !== void 0 ? _a : []).concat(getMultiskillExpansionKeys(metricKey, metric));
    };
    var toggleRows = function (rowKey, descendantKeys) {
        if (descendantKeys === void 0) { descendantKeys = []; }
        var expanded = !expandedRows[rowKey];
        setExpandedRows(function (prev) {
            var _a;
            return (__assign(__assign(__assign(__assign({}, prev), (_a = {}, _a[rowKey] = expanded, _a)), Object.fromEntries(Object.keys(prev)
                .filter(function (key) { return key.startsWith("".concat(rowKey, "|")); })
                .map(function (key) { return [key, false]; }))), Object.fromEntries(descendantKeys.map(function (key) { return [key, false]; }))));
        });
    };
    var renderCountryRows = function (parentKey, countryRows, metric, countryDepth, isMultiskill, useMultiskillLabelColor) {
        if (countryDepth === void 0) { countryDepth = 4; }
        if (isMultiskill === void 0) { isMultiskill = false; }
        if (useMultiskillLabelColor === void 0) { useMultiskillLabelColor = false; }
        return countryRows.map(function (country, countryIndex) {
            var countryName = getCountryName(country);
            var countryKey = getCountryKey(parentKey, country, countryIndex);
            var isCountryExpanded = !!expandedRows[countryKey];
            var countryLocations = country.locations || [];
            var countryColor = useMultiskillLabelColor ? MULTISKILL_COLOR : theme.palette.common.white;
            var locationColor = useMultiskillLabelColor ? MULTISKILL_COLOR : theme.palette.background.default;
            return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, __assign({ component: "th", stickySizes: LEVEL_ROW_STICKIES, "aria-label": DAILY_NEST_LEVELS[countryDepth], color: countryColor }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: isCountryExpanded, onToggle: countryLocations.length > 0 ? function () { return toggleRows(countryKey); } : undefined, bold: true }, { children: countryName })) })), renderValueCells(__assign(__assign({}, country), { isMultiskillMember: isMultiskill }), metric, 'country')] }), isCountryExpanded &&
                        countryLocations.map(function (location, locationIndex) { return (_jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, __assign({ component: "th", stickySizes: LEVEL_ROW_STICKIES, "aria-label": DAILY_NEST_LEVELS[nextTableHierarchyDepth(countryDepth)], color: locationColor }, { children: _jsx(NormalWeightLabel, { children: _jsx(RumEntityLabel, __assign({ kind: "planLocation", id: location.planLocationId }, { children: getLocationName(location) })) }) })), renderValueCells(__assign(__assign({}, location), { isMultiskillMember: isMultiskill }), metric, 'location')] }, "".concat(countryKey, "|location:").concat(getLocationKey(location, locationIndex)))); })] }, countryKey));
        });
    };
    var renderGroupedRows = function (metricKey, metric) {
        return queuesByLob === null || queuesByLob === void 0 ? void 0 : queuesByLob.map(function (lob, lobIndex) {
            var lobName = getLobName(lob);
            var lobKey = getLobKey(metricKey, lob, lobIndex);
            var isLobExpanded = !!expandedRows[lobKey];
            var queueOnly = isQueueOnlyMetric(metric);
            var visibleQueues = getVisibleQueues(metric, lob.queues);
            // Skip LOB if no visible queues
            if (visibleQueues.length === 0)
                return null;
            return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, __assign({ component: "th", color: theme.palette.grey[400], stickySizes: LEVEL_ROW_STICKIES, "aria-label": LEVEL_ROW_INDENTS.lob }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: isLobExpanded, onToggle: visibleQueues.length
                                        ? function () { return toggleRows(lobKey, getQueueExpansionKeys(lobKey, visibleQueues, metric)); }
                                        : undefined }, { children: _jsx(NormalWeightLabel, { children: _jsx(RumEntityLabel, __assign({ kind: "lob", id: lobName ? lob.lobId : undefined }, { children: lobName || t('period.unassignedLob', { defaultValue: 'Unassigned LOB' }) })) }) })) })), renderBlankCells(theme.palette.grey[400])] }), isLobExpanded &&
                        visibleQueues.map(function (queue, queueIndex) {
                            var queueName = getQueueName(queue);
                            var queueKey = getQueueKey(lobKey, queue, queueIndex);
                            var isQueueExpanded = !!expandedRows[queueKey];
                            var queueCountries = queueOnly ? [] : queue.countries || [];
                            return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, __assign({ component: "th", stickySizes: LEVEL_ROW_STICKIES, "aria-label": LEVEL_ROW_INDENTS.queue, color: theme.palette.common.white }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: isQueueExpanded, onToggle: queueCountries.length > 0
                                                        ? function () { return toggleRows(queueKey, getCountryExpansionKeys(queueKey, queueCountries)); }
                                                        : undefined, bold: true }, { children: _jsxs("span", { children: [_jsx(RumEntityLabel, __assign({ kind: "queue", id: queue.queueId }, { children: queueName })), getQueueTargetFlag(queue, metric)] }) })) })), renderValueCells(queue, metric, 'queue')] }), isQueueExpanded &&
                                        renderCountryRows(queueKey, queueCountries, metric, 4, queue.queueType === QueueType.Multiskill || queue.isMultiskillMember)] }, queueKey));
                        })] }, lobKey));
        });
    };
    var renderMultiskillGroupRows = function (metricKey, metric) {
        var groupQueues = getMultiskillGroupQueues(metric);
        if (groupQueues.length === 0)
            return null;
        var groupSectionKey = getMultiskillSectionKey(metricKey);
        var isGroupSectionExpanded = !!expandedRows[groupSectionKey];
        var queueOnly = isQueueOnlyMetric(metric);
        return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, __assign({ component: "th", color: TABLE_HIERARCHY_COLORS.lightBlueGray, stickySizes: LEVEL_ROW_STICKIES, "aria-label": LEVEL_ROW_INDENTS.lob }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: isGroupSectionExpanded, onToggle: function () { return toggleRows(groupSectionKey, getMultiskillGroupExpansionKeys(metricKey, metric)); }, bold: true }, { children: t('period.multiskill', { defaultValue: 'Multi-skill' }) })) })), renderBlankCells(TABLE_HIERARCHY_COLORS.lightBlueGray)] }), isGroupSectionExpanded &&
                    groupQueues.map(function (queue, groupIndex) {
                        var queueKey = getMultiskillGroupKey(metricKey, queue, groupIndex);
                        var queueCountries = queueOnly ? [] : queue.countries || [];
                        return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, __assign({ component: "th", stickySizes: LEVEL_ROW_STICKIES, "aria-label": LEVEL_ROW_INDENTS.queue, color: MULTISKILL_COLOR }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: !!expandedRows[queueKey], onToggle: queueCountries.length > 0
                                                    ? function () { return toggleRows(queueKey, getCountryExpansionKeys(queueKey, queueCountries)); }
                                                    : undefined, bold: true }, { children: _jsx(RumEntityLabel, __assign({ kind: "multiskillGroup", id: queue.queueId }, { children: getQueueName(queue) })) })) })), renderValueCells(queue, metric, 'queue')] }), expandedRows[queueKey] &&
                                    renderCountryRows(queueKey, queueCountries, metric, 4, queue.queueType === QueueType.Multiskill || queue.isMultiskillMember, true)] }, queueKey));
                    })] }, "multiskill-section"));
    };
    return (_jsx(_Fragment, { children: metricsToRender.map(function (metric) {
            var metricKey = "metric:".concat(metric.key);
            var isExpanded = !!expandedRows[metricKey];
            var isExpandable = EXPANDABLE_METRIC_KEYS.includes(metric.key);
            var queueOnly = isQueueOnlyMetric(metric);
            var showExpansion = isExpandable && canExpandMetric(metric);
            var isSeatsCountryHierarchy = usesSeatsCountryHierarchy(metric);
            var expandedRowsContent = queueOnly
                ? null
                : renderCountryRows(metricKey, countries, metric, 2);
            if (isSeatsCountryHierarchy) {
                expandedRowsContent = (_jsxs(_Fragment, { children: [renderCountryRows(metricKey, countries, metric, 2), renderMultiskillGroupRows(metricKey, metric)] }));
            }
            else if (hasGroupedHierarchy) {
                expandedRowsContent = (_jsxs(_Fragment, { children: [renderGroupedRows(metricKey, metric), renderMultiskillGroupRows(metricKey, metric)] }));
            }
            return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, __assign({ component: "th", color: bodyColor, stickySizes: LEVEL_ROW_STICKIES, "aria-label": hasAnyExpansion ? LEVEL_ROW_INDENTS.metric : undefined, bold: true }, { children: _jsx(ExpandableRowLabel, __assign({ expanded: isExpanded, onToggle: showExpansion ? function () { return toggleRows(metricKey, getMetricExpansionKeys(metric)); } : undefined }, { children: t("daily.reportRows.".concat(metric.key), { defaultValue: metric.name }) })) })), renderValueCells({ detail: detail, countries: countries }, metric, 'metric')] }), isExpanded && showExpansion && expandedRowsContent] }, metricKey));
        }) }));
};
